/**
 * Feature Modals for AIQIA DataVision
 * Contains modals for: Format Conversion, Code Generation, Export, Share, Validation, Diff
 */

import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useEditorStore } from '@/store'
import type { DataFormat } from '@/types'
import { convertFormat, getFormatExtension } from '@/utils/serializers'
import { generateCode, getLanguageLabel, type CodeGenLanguage } from '@/utils/codegen'
import { exportToPNG, exportToSVG, downloadDataUrl, downloadSVG, downloadText, copyToClipboard, generateFilename } from '@/utils/exporters'
import { validateJsonSchema, parseJsonSchema, type ValidationError } from '@/utils/validation'
import { calculateDiff, getChangedEntries, getDiffColor, getDiffIcon, formatDiffValue, type DiffResult } from '@/utils/diff'

// Shared Modal component
function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md'
}: {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
}) {
  if (!isOpen) return null

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white dark:bg-slate-800 rounded-lg shadow-xl ${sizeClasses[size]} w-full mx-4 max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between p-4 border-b dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {children}
        </div>
      </div>
    </div>
  )
}

// ============== Convert Format Modal ==============

export function ConvertFormatModal({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { parsedData, metadata, setContent, setFormat, parseAndRender } = useEditorStore()
  const [targetFormat, setTargetFormat] = useState<DataFormat>('json')
  const [preview, setPreview] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const formats: DataFormat[] = ['json', 'yaml', 'xml', 'toml']

  useEffect(() => {
    if (!parsedData) {
      setPreview('')
      setError('No data to convert. Please parse some content first.')
      return
    }

    try {
      const converted = convertFormat(parsedData, targetFormat)
      setPreview(converted)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Conversion failed')
      setPreview('')
    }
  }, [parsedData, targetFormat])

  const handleApply = () => {
    if (preview) {
      setContent(preview)
      setFormat(targetFormat)
      parseAndRender()
      onClose()
    }
  }

  const handleDownload = () => {
    if (preview) {
      const filename = generateFilename('datavision-converted', getFormatExtension(targetFormat))
      downloadText(preview, filename)
    }
  }

  const handleCopy = async () => {
    if (preview) {
      await copyToClipboard(preview)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('features.convert.title', 'Convert Format')} size="lg">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            {t('features.convert.from', 'Current Format')}: <span className="font-bold text-primary-600">{metadata?.format?.toUpperCase() || 'Unknown'}</span>
          </label>

          <label className="block text-sm font-medium mb-2">{t('features.convert.to', 'Convert to')}:</label>
          <div className="flex gap-2">
            {formats.map((fmt) => (
              <button
                key={fmt}
                onClick={() => setTargetFormat(fmt)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  targetFormat === fmt
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                {fmt.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        {preview && (
          <div>
            <label className="block text-sm font-medium mb-2">{t('features.convert.preview', 'Preview')}:</label>
            <pre className="bg-slate-100 dark:bg-slate-900 p-4 rounded-lg text-sm overflow-auto max-h-64 font-mono">
              {preview.slice(0, 2000)}{preview.length > 2000 ? '\n...(truncated)' : ''}
            </pre>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t dark:border-slate-700">
          <button onClick={handleCopy} className="btn-ghost" disabled={!preview}>
            {t('actions.copy', 'Copy')}
          </button>
          <button onClick={handleDownload} className="btn-ghost" disabled={!preview}>
            {t('actions.download', 'Download')}
          </button>
          <button onClick={handleApply} className="btn-primary" disabled={!preview}>
            {t('features.convert.apply', 'Apply to Editor')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ============== Code Generation Modal ==============

export function CodeGenModal({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { parsedData } = useEditorStore()
  const [language, setLanguage] = useState<CodeGenLanguage>('typescript')
  const [rootName, setRootName] = useState('Root')
  const [code, setCode] = useState<string>('')
  const [copied, setCopied] = useState(false)

  const languages: CodeGenLanguage[] = ['typescript', 'zod', 'go', 'python', 'rust']

  useEffect(() => {
    if (!parsedData) {
      setCode('// No data available. Please parse some content first.')
      return
    }

    try {
      const generated = generateCode(parsedData, language, rootName)
      setCode(generated)
    } catch (err) {
      setCode(`// Error: ${err instanceof Error ? err.message : 'Generation failed'}`)
    }
  }, [parsedData, language, rootName])

  const handleCopy = async () => {
    await copyToClipboard(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDownload = () => {
    const ext = language === 'typescript' || language === 'zod' ? 'ts' :
                language === 'go' ? 'go' :
                language === 'python' ? 'py' :
                language === 'rust' ? 'rs' : 'txt'
    const filename = generateFilename(`${rootName.toLowerCase()}-types`, ext)
    downloadText(code, filename)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('features.codegen.title', 'Generate Code')} size="xl">
      <div className="space-y-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2">{t('features.codegen.language', 'Language')}:</label>
            <div className="flex flex-wrap gap-2">
              {languages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    language === lang
                      ? 'bg-primary-600 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {getLanguageLabel(lang)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('features.codegen.rootName', 'Root Name')}:</label>
            <input
              type="text"
              value={rootName}
              onChange={(e) => setRootName(e.target.value || 'Root')}
              className="px-3 py-1.5 border rounded-lg dark:bg-slate-700 dark:border-slate-600 w-32"
              placeholder="Root"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium">{t('features.codegen.output', 'Generated Code')}:</label>
            <span className="text-xs text-slate-500">{code.split('\n').length} lines</span>
          </div>
          <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm overflow-auto max-h-96 font-mono">
            <code>{code}</code>
          </pre>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t dark:border-slate-700">
          <button onClick={handleCopy} className="btn-ghost flex items-center gap-2">
            {copied ? (
              <>
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('actions.copied', 'Copied!')}
              </>
            ) : (
              t('actions.copy', 'Copy')
            )}
          </button>
          <button onClick={handleDownload} className="btn-primary">
            {t('actions.download', 'Download')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ============== Export Modal ==============

export function ExportModal({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { nodes, theme } = useEditorStore()
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleExport = async (format: 'png' | 'svg') => {
    const graphElement = document.querySelector('.react-flow') as HTMLElement
    if (!graphElement || nodes.length === 0) {
      setError('No graph to export')
      return
    }

    setExporting(true)
    setError(null)

    try {
      const bgColor = theme === 'dark' ? '#0f172a' : '#f8fafc'

      if (format === 'png') {
        const dataUrl = await exportToPNG(graphElement, { backgroundColor: bgColor })
        const filename = generateFilename('datavision-graph', 'png')
        downloadDataUrl(dataUrl, filename)
      } else {
        const dataUrl = await exportToSVG(graphElement, { backgroundColor: bgColor })
        const filename = generateFilename('datavision-graph', 'svg')
        downloadSVG(dataUrl, filename)
      }

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('features.export.title', 'Export Graph')} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('features.export.description', 'Choose a format to export your graph visualization.')}
        </p>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => handleExport('png')}
            disabled={exporting || nodes.length === 0}
            className="flex flex-col items-center gap-2 p-4 border-2 border-dashed rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50"
          >
            <svg className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="font-medium">PNG</span>
            <span className="text-xs text-slate-500">Raster image</span>
          </button>

          <button
            onClick={() => handleExport('svg')}
            disabled={exporting || nodes.length === 0}
            className="flex flex-col items-center gap-2 p-4 border-2 border-dashed rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50"
          >
            <svg className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            <span className="font-medium">SVG</span>
            <span className="text-xs text-slate-500">Vector image</span>
          </button>
        </div>

        {exporting && (
          <div className="text-center text-sm text-slate-500">
            Exporting...
          </div>
        )}
      </div>
    </Modal>
  )
}

// ============== Share Modal ==============

export function ShareModal({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { content, format } = useEditorStore()
  const [shareLink, setShareLink] = useState<string>('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (content) {
      // Create shareable link with base64 encoded content
      const encoded = btoa(unescape(encodeURIComponent(content)))
      const params = new URLSearchParams({
        data: encoded,
        format: format
      })
      const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`
      setShareLink(url)
    }
  }, [content, format])

  const handleCopy = async () => {
    await copyToClipboard(shareLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('features.share.title', 'Share Visualization')} size="md">
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('features.share.description', 'Share this link to let others view your data visualization.')}
        </p>

        {!content ? (
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 rounded-lg text-sm">
            {t('features.share.noContent', 'No content to share. Please add some data first.')}
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium mb-2">{t('features.share.link', 'Shareable Link')}:</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="flex-1 px-3 py-2 border rounded-lg bg-slate-50 dark:bg-slate-900 dark:border-slate-600 text-sm font-mono"
                />
                <button onClick={handleCopy} className="btn-primary whitespace-nowrap">
                  {copied ? t('actions.copied', 'Copied!') : t('actions.copy', 'Copy')}
                </button>
              </div>
            </div>

            <div className="text-xs text-slate-500 dark:text-slate-400">
              <p>{t('features.share.note', 'Note: The data is encoded in the URL. Very large datasets may exceed URL length limits.')}</p>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

// ============== Validation Modal ==============

export function ValidationModal({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { parsedData } = useEditorStore()
  const [schemaInput, setSchemaInput] = useState('')
  const [result, setResult] = useState<{ valid: boolean; errors: ValidationError[] } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleValidate = useCallback(() => {
    if (!parsedData) {
      setError('No data to validate. Please parse some content first.')
      return
    }

    if (!schemaInput.trim()) {
      setError('Please enter a JSON Schema.')
      return
    }

    try {
      const schema = parseJsonSchema(schemaInput)
      const validationResult = validateJsonSchema(parsedData, schema)
      setResult(validationResult)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed')
      setResult(null)
    }
  }, [parsedData, schemaInput])

  const sampleSchema = JSON.stringify({
    type: 'object',
    required: ['name'],
    properties: {
      name: { type: 'string', minLength: 1 },
      age: { type: 'number', minimum: 0 },
      email: { type: 'string', format: 'email' }
    }
  }, null, 2)

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('features.validation.title', 'Validate Schema')} size="lg">
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium">{t('features.validation.schema', 'JSON Schema')}:</label>
            <button
              onClick={() => setSchemaInput(sampleSchema)}
              className="text-xs text-primary-600 hover:underline"
            >
              {t('features.validation.loadSample', 'Load sample schema')}
            </button>
          </div>
          <textarea
            value={schemaInput}
            onChange={(e) => setSchemaInput(e.target.value)}
            placeholder={t('features.validation.placeholder', 'Paste your JSON Schema here...')}
            className="w-full h-40 px-3 py-2 border rounded-lg font-mono text-sm dark:bg-slate-900 dark:border-slate-600"
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        {result && (
          <div className={`p-4 rounded-lg ${result.valid ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
            <div className="flex items-center gap-2 mb-2">
              {result.valid ? (
                <>
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium text-green-600 dark:text-green-400">
                    {t('features.validation.valid', 'Valid!')}
                  </span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="font-medium text-red-600 dark:text-red-400">
                    {t('features.validation.invalid', 'Invalid')} ({result.errors.length} errors)
                  </span>
                </>
              )}
            </div>

            {result.errors.length > 0 && (
              <div className="mt-2 space-y-1 max-h-40 overflow-auto">
                {result.errors.map((err, i) => (
                  <div key={i} className="text-sm text-red-600 dark:text-red-400 font-mono">
                    <span className="font-bold">{err.path}</span>: {err.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t dark:border-slate-700">
          <button onClick={onClose} className="btn-ghost">
            {t('actions.close', 'Close')}
          </button>
          <button onClick={handleValidate} className="btn-primary" disabled={!parsedData}>
            {t('features.validation.validate', 'Validate')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ============== Diff Modal ==============

export function DiffModal({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { parsedData } = useEditorStore()
  const [compareInput, setCompareInput] = useState('')
  const [result, setResult] = useState<DiffResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleCompare = useCallback(() => {
    if (!parsedData) {
      setError('No data to compare. Please parse some content first.')
      return
    }

    if (!compareInput.trim()) {
      setError('Please enter data to compare.')
      return
    }

    try {
      const compareData = JSON.parse(compareInput)
      const diffResult = calculateDiff(parsedData, compareData)
      setResult(diffResult)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Comparison failed')
      setResult(null)
    }
  }, [parsedData, compareInput])

  const changedEntries = result ? getChangedEntries(result) : []

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('features.diff.title', 'Compare Data')} size="xl">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">{t('features.diff.current', 'Current Data')}:</label>
            <pre className="bg-slate-100 dark:bg-slate-900 p-3 rounded-lg text-xs overflow-auto max-h-40 font-mono">
              {parsedData ? JSON.stringify(parsedData, null, 2).slice(0, 1000) : 'No data'}
              {parsedData && JSON.stringify(parsedData).length > 1000 ? '...' : ''}
            </pre>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('features.diff.compare', 'Compare With')}:</label>
            <textarea
              value={compareInput}
              onChange={(e) => setCompareInput(e.target.value)}
              placeholder={t('features.diff.placeholder', 'Paste JSON to compare...')}
              className="w-full h-40 px-3 py-2 border rounded-lg font-mono text-xs dark:bg-slate-900 dark:border-slate-600"
            />
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-sm">
              <span className="font-medium">{t('features.diff.similarity', 'Similarity')}: {result.similarity}%</span>
              <span className="text-green-600">+{result.summary.added} added</span>
              <span className="text-red-600">-{result.summary.removed} removed</span>
              <span className="text-yellow-600">~{result.summary.modified} modified</span>
            </div>

            {changedEntries.length > 0 && (
              <div className="max-h-60 overflow-auto border dark:border-slate-700 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left w-8"></th>
                      <th className="px-3 py-2 text-left">{t('features.diff.path', 'Path')}</th>
                      <th className="px-3 py-2 text-left">{t('features.diff.oldValue', 'Old')}</th>
                      <th className="px-3 py-2 text-left">{t('features.diff.newValue', 'New')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changedEntries.slice(0, 50).map((entry, i) => (
                      <tr key={i} className={`border-t dark:border-slate-700 ${getDiffColor(entry.type)}`}>
                        <td className="px-3 py-1 font-mono font-bold">{getDiffIcon(entry.type)}</td>
                        <td className="px-3 py-1 font-mono text-xs">{entry.path}</td>
                        <td className="px-3 py-1 font-mono text-xs max-w-[200px] truncate">
                          {entry.oldValue !== undefined ? formatDiffValue(entry.oldValue) : '-'}
                        </td>
                        <td className="px-3 py-1 font-mono text-xs max-w-[200px] truncate">
                          {entry.newValue !== undefined ? formatDiffValue(entry.newValue) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {changedEntries.length > 50 && (
                  <div className="p-2 text-center text-xs text-slate-500">
                    Showing 50 of {changedEntries.length} changes
                  </div>
                )}
              </div>
            )}

            {changedEntries.length === 0 && (
              <div className="text-center py-4 text-green-600">
                {t('features.diff.identical', 'The data is identical!')}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t dark:border-slate-700">
          <button onClick={onClose} className="btn-ghost">
            {t('actions.close', 'Close')}
          </button>
          <button onClick={handleCompare} className="btn-primary" disabled={!parsedData}>
            {t('features.diff.compare', 'Compare')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
